import React, { useState } from "react";
import { View, ScrollView, StyleSheet } from "react-native";
import {
  Tag,
  Text,
  Input,
  Error,
  Picker,
  Button,
  Loading,
  InputLabel,
  MultiSelector,
  ParentCatPicker,
  CityMultiSelector,
  CoWorkerMultiSelector,
} from "components";
import ReactNativeModal from "react-native-modal";

import css from "styles";
import { colors, strings } from "assets/constants";

import {
  useGetEmployees,
  useCustomerCategories,
  useCustomerBaseOptions,
} from "clients/apis";
import { sn } from "utils";

type StateItem = {
  id: null | number;
  title?: string;
  name?: string;
  text?: string;
};

export type Filters = {
  mobile?: string;
  customerTypes?: StateItem;
  friendships?: Array<StateItem>;
  cities?: Array<StateItem>;
  popularGroups?: StateItem;
  customerGroups?: Array<StateItem>;
  colleagueCompanies?: Array<StateItem>;
  creators?: Array<StateItem>;
  noAddress?: "true";
};

type Props = {
  value?: Filters;
  onApply: (filters: Filters) => void;
  close: () => void;
};

const unregisteredItem: StateItem = {
  id: null,
  title: strings.unregistered,
  name: strings.unregistered,
  text: strings.unregistered,
};

const CustomerReportFilters: React.FC<Props> = (props) => {
  const { close, onApply, value } = props;

  const {
    data: customerBaseOptions,
    isLoading: customerBaseOptionsIsLoading,
    isError: customerBaseOptionsIsError,
    error: customerBaseOptionsError,
    refetch: customerBaseOptionsRefetch,
    isRefetching: customerBaseOptionsIsRefetching,
  } = useCustomerBaseOptions();
  const {
    data: customerGroupList,
    isLoading: customerGroupListIsLoading,
    isError: customerGroupListIsError,
    error: customerGroupListError,
    refetch: customerGroupListRefetch,
    isRefetching: customerGroupListIsRefetching,
  } = useCustomerCategories();
  const {
    data: employees,
    isLoading: employeesIsLoading,
    isError: employeesIsError,
    error: employeesError,
    refetch: employeesRefetch,
    isRefetching: employeesIsRefetching,
  } = useGetEmployees();

  const [mobile, setMobile] = useState<string>(value?.mobile);
  const [customerTypes, setCustomerTypes] = useState<StateItem>(
    value?.customerTypes
  );
  const [friendships, setFriendships] = useState<Array<StateItem>>(
    value?.friendships
  );
  const [cities, setCities] = useState<Array<StateItem>>(value?.cities);
  const [customerGroups, setCustomerGroups] = useState<Array<StateItem>>(
    value?.customerGroups
  );
  const [colleagueCompanies, setColleagueCompanies] = useState<
    Array<StateItem>
  >(value?.colleagueCompanies);
  const [creators, setCreators] = useState<Array<StateItem>>(value?.creators);
  const [popularGroups, setPopularGroups] = useState<StateItem>(
    value?.popularGroups
  );

  const _onRemoveMobile = () => setMobile("");
  const _onRemoveCustomerTypes = () => setCustomerTypes(undefined);
  const _onRemoveFriendship = (id: number | null) =>
    setFriendships(friendships?.filter((e) => e.id !== id));
  const _onRemovePopularGroups = () => setPopularGroups(undefined);
  const _onRemoveCity = (id: number | null) =>
    setCities(cities?.filter((c) => c.id !== id));
  const _onRemoveCustomerGroup = (id: number | null) =>
    setCustomerGroups(customerGroups?.filter((e) => e.id !== id));
  const _onRemoveCreator = (id: number | null) =>
    setCreators(customerGroups?.filter((c) => c.id !== id));
  const _onRemoveColleagueCompany = (id: number | null) =>
    setColleagueCompanies(colleagueCompanies?.filter((c) => c.id !== id));

  const _onApply = () => {
    onApply({
      mobile,
      customerTypes,
      cities,
      colleagueCompanies,
      creators,
      customerGroups,
      friendships,
      popularGroups,
    });
    close();
  };

  const _renderContent = () => {
    if (
      customerBaseOptionsIsLoading ||
      customerGroupListIsLoading ||
      employeesIsLoading
    ) {
      return <Loading />;
    }

    if (
      customerBaseOptionsIsError ||
      customerGroupListIsError ||
      employeesIsError
    ) {
      return (
        <Error
          error={customerBaseOptionsError}
          reset={() => {
            customerBaseOptionsRefetch();
            customerGroupListRefetch();
            employeesRefetch();
          }}
          loading={
            customerBaseOptionsIsRefetching ||
            customerGroupListIsRefetching ||
            employeesIsRefetching
          }
        />
      );
    }

    return (
      <>
        <View style={styles.selectedFiltersContainer}>
          <View style={css.rRowCenterAlign}>
            <Text weight="bold">فیلترهای انتخاب شده</Text>
            {mobile === "" &&
              customerTypes === undefined &&
              popularGroups === undefined &&
              creators?.length +
                colleagueCompanies?.length +
                customerGroups?.length +
                friendships?.length +
                cities?.length <=
                0 && (
                <Text
                  align="center"
                  style={{ marginHorizontal: 5 }}
                  weight="bold"
                  color={colors.text.gray}
                >
                  (فیلتری انتخاب نشده است!)
                </Text>
              )}
          </View>

          <View style={{ ...css.rRowCenterAlign, ...css.wrap, marginTop: 5 }}>
            {mobile !== "" && (
              <View style={styles.tagsContainer}>
                <Text weight="medium">{strings.mobile}: </Text>
                <Tag value={mobile} onPress={_onRemoveMobile} />
              </View>
            )}

            {customerTypes !== undefined && (
              <View style={styles.tagsContainer}>
                <Text weight="medium">{strings.customer_type}: </Text>
                <Tag
                  value={customerTypes.title}
                  onPress={_onRemoveCustomerTypes}
                />
              </View>
            )}

            {friendships?.length > 0 && (
              <View style={styles.tagsContainer}>
                <Text weight="medium">{strings.familiarity_way}: </Text>
                <View style={css.rRowCenterAlign}>
                  {friendships.map((f, k) => (
                    <Tag
                      key={String(k)}
                      value={f.title}
                      onPress={() => _onRemoveFriendship(f.id)}
                    />
                  ))}
                </View>
              </View>
            )}

            {cities?.length > 0 && (
              <View style={styles.tagsContainer}>
                <Text weight="medium">{strings.city}: </Text>
                <View style={css.rRowCenterAlign}>
                  {cities.map((c, k) => (
                    <Tag
                      key={String(k)}
                      value={c.text}
                      onPress={() => _onRemoveCity(c.id)}
                    />
                  ))}
                </View>
              </View>
            )}

            {popularGroups !== undefined && (
              <View style={styles.tagsContainer}>
                <Text weight="medium">{strings.popular_category}: </Text>
                <Tag
                  value={popularGroups.title}
                  onPress={_onRemovePopularGroups}
                />
              </View>
            )}

            {customerGroups?.length > 0 && (
              <View style={styles.tagsContainer}>
                <Text weight="medium">{strings.customer_category}: </Text>
                <View style={css.rRowCenterAlign}>
                  {customerGroups.map((c, k) => (
                    <Tag
                      key={String(k)}
                      value={c.title}
                      onPress={() => _onRemoveCustomerGroup(c.id)}
                    />
                  ))}
                </View>
              </View>
            )}

            {colleagueCompanies?.length > 0 && (
              <View style={styles.tagsContainer}>
                <Text weight="medium">{strings.co_workers}: </Text>
                <View style={css.rRowCenterAlign}>
                  {colleagueCompanies.map((co, k) => (
                    <Tag
                      key={String(k)}
                      value={co.name}
                      onPress={() => _onRemoveColleagueCompany(co.id)}
                    />
                  ))}
                </View>
              </View>
            )}

            {creators?.length > 0 && (
              <View style={styles.tagsContainer}>
                <Text weight="medium">{strings.creator}: </Text>
                <View style={css.rRowCenterAlign}>
                  {creators.map((c, k) => (
                    <Tag
                      key={String(k)}
                      value={c.name}
                      onPress={() => _onRemoveCreator(c.id)}
                    />
                  ))}
                </View>
              </View>
            )}
          </View>
        </View>

        <ScrollView contentContainerStyle={css.responsiveInputsListContainer}>
          {/* mobile */}
          <View style={styles.inputContainer}>
            <View>
              {mobile !== "" && <View style={styles.dot} />}
              <InputLabel label={strings.mobile} />
            </View>
            <Input
              value={mobile}
              onChangeText={setMobile}
              keyboardType="phone-pad"
              icon={{ name: "smartphone" }}
              placeholder={strings?.insert_mobile_number}
              hideError
            />
          </View>

          {/* customer type */}
          {customerBaseOptions !== undefined && (
            <View style={styles.inputContainer}>
              <View>
                {customerTypes !== undefined && <View style={styles.dot} />}
                <InputLabel label={strings.customer_type} />
              </View>
              <Picker
                placeholder={strings.pick_customer_type}
                data={customerBaseOptions?.data?.status}
                value={customerTypes?.title}
                onSelect={setCustomerTypes}
                icon={{ name: "list" }}
                hideError
              />
            </View>
          )}

          {/* friendship */}
          {customerBaseOptions !== undefined && (
            <View style={styles.inputContainer}>
              <View>
                {friendships?.length > 0 && <View style={styles.dot} />}
                <InputLabel label={strings.familiarity_way} />
              </View>
              <MultiSelector
                data={[
                  unregisteredItem,
                  ...customerBaseOptions?.data?.frindship?.original?.data
                    ?.items,
                ]}
                value={friendships}
                onApply={setFriendships}
                icon={{ name: "list" }}
              />
            </View>
          )}

          {/* customer group */}
          {customerGroupList !== undefined && (
            <View style={styles.inputContainer}>
              <View>
                {customerGroups?.length > 0 && <View style={styles.dot} />}
                <InputLabel label={strings.customer_category} />
              </View>

              <MultiSelector
                data={customerGroupList?.data?.data}
                value={customerGroups}
                onApply={setCustomerGroups}
                icon={{ name: "list" }}
              />
            </View>
          )}

          {/* city */}
          <View style={styles.inputContainer}>
            <View>
              {cities?.length > 0 && <View style={styles.dot} />}
              <InputLabel label={strings.city} />
            </View>
            <CityMultiSelector value={cities} onApply={setCities} />
          </View>

          {/* co workers */}
          <View style={styles.inputContainer}>
            <View>
              {colleagueCompanies?.length > 0 && <View style={styles.dot} />}
              <InputLabel label={strings.co_workers} />
            </View>

            <CoWorkerMultiSelector
              value={colleagueCompanies}
              onApply={setColleagueCompanies}
            />
          </View>

          {/* creators */}
          {employees && (
            <View style={styles.inputContainer}>
              <View>
                {creators?.length > 0 && <View style={styles.dot} />}
                <InputLabel label={strings.creator} />
              </View>

              <MultiSelector
                data={[
                  ...employees?.data?.data?.map((e: any) => ({
                    id: e?.user?.id,
                    name: e?.user?.name,
                  })),
                ]}
                value={creators}
                onApply={setCreators}
                icon={{ name: "list" }}
              />
            </View>
          )}

          {/* popular group */}
          <View style={styles.inputContainer}>
            <View>
              {popularGroups !== undefined && <View style={styles.dot} />}
              <InputLabel label={strings.popular_category} />
            </View>

            <ParentCatPicker
              showUnregisteredItem
              showChild
              hideParents
              value={popularGroups}
              onSelect={setPopularGroups}
              hideError
            />
          </View>
        </ScrollView>
        <View style={css.rowCenterAlign}>
          <Button
            onPress={_onApply}
            width={sn("40%", "40%", 200, 200)}
            style={{ margin: 12 }}
            title={{ value: strings.apply_filters }}
          />

          <Button
            onPress={close}
            width={sn("40%", "40%", 200, 200)}
            style={{ margin: 12 }}
            bgColor={colors.transparent}
            bordered={{ width: 1, color: colors.danger }}
            title={{ value: strings.cancel, color: colors.danger }}
          />
        </View>
      </>
    );
  };

  return (
    <ReactNativeModal
      isVisible={true}
      animationIn={"slideInRight"}
      animationOut={"slideOutRight"}
      backdropColor={colors.bg.screen}
      backdropOpacity={1}
      style={{ margin: 0, backgroundColor: colors.bg.screen }}
    >
      <>{_renderContent()}</>
    </ReactNativeModal>
  );
};

export default CustomerReportFilters;

const styles = StyleSheet.create({
  inputContainer: {
    ...css.responsiveInputContainer,
    marginBottom: 25,
  },

  tag: {
    flexDirection: "row-reverse",
    borderRadius: 10,
    backgroundColor: "#fff",
    alignItems: "center",
    alignSelf: "flex-start",
    marginHorizontal: 20,
    paddingHorizontal: 15,
  },

  tagsContainer: {
    flexDirection: "row-reverse",
    alignItems: "center",
    flexWrap: "wrap",
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    padding: 2,
    paddingRight: 4,
    margin: 3,
    borderWidth: 1,
    borderColor: "#f2f2f2",
  },

  selectedFiltersContainer: {
    margin: 12,
    borderWidth: 1,
    borderColor: "#aaa",
    padding: 12,
    borderRadius: 10,
  },

  dot: {
    position: "absolute",
    width: 7,
    height: 7,
    borderRadius: 7,
    backgroundColor: "red",
    alignSelf: "flex-end",
    top: 3,
  },
});
